import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";

//Controls
import { Grid, Icon } from "@mui/material";
import { Timezone } from 'Components/uiControls';
import { Pill, TableButtons } from "@gpstrackit/gtc-react-controls";
import useExport from "Components/Table/Export";

//Slice
import { workOrdersSettingsSearchHistoryLogs, workOrdersSettingsSearchStatuses } from './redux/slice';
import MapModal from "Modules/.common/components/MapModal";

//Styles
import './index.scss';

//Helpers
import { GetFormatForMoment, splitTextIntoLines } from 'Core/data/Helpers';

//Variables
const allowedKeys = ["action", "statusId", "updatedBy", "updatedOn", "dynamicFormAnswerId"];
const HistoryLogs = ({ tab }) => {

    const { messages } = useIntl();
    const dispatch = useDispatch();

    //Local States
    const [dataToMap, setDataToMap] = useState({});

    //Redux States
    const { open, workOrderId } = useSelector((state) => state.workOrdersCreateEditWorkOrderRedux);
    const { historyLogs, workOrderStatus } = useSelector((state) => state.workOrdersHistoryLogsWorkOrderRedux);
    const user = useSelector((state) => state.securityRedux.user);
    const { dateformat, timeZone } = user;

    const format = GetFormatForMoment(dateformat);

    useEffect(() => {
        if (open && tab === 2) {
            dispatch(workOrdersSettingsSearchHistoryLogs({ workOrderId: workOrderId }))
            dispatch(workOrdersSettingsSearchStatuses({}));
        }
    }, [open, tab])

    const validateObject = useMemo(
        () => (obj) => {
            const objKeys = Object.keys(obj);
            if (objKeys.length !== allowedKeys.length) return false;
            return objKeys.every((key) => allowedKeys.includes(key));
        },
        []
    );

    const getHeaders = () => {
        return [
            messages['workOrder_comments_createBy'],
            messages['workOrder_comments_createOn'],
            messages['workOrder_historyLogs_action'],
            messages['workOrder_historyLogs_changes']
        ];
    };

    const keyAliases = {
        "nameCreatedBy.name": messages['workOrder_historyLogs_createdBy'],
        "customerLocation.address.address": messages['workOrder_historyLogs_address'],
        "customer.name": messages['workOrder_historyLogs_customerName'],
        "description": messages['workOrder_historyLogs_description'],
        "severity": messages['workOrder_historyLogs_severity'],
        "customerLocation.name": messages['workOrder_historyLogs_locationName'],
        "customerContact.name": messages['workOrder_historyLogs_contactName'],
        "customerContact.email": messages['workOrder_historyLogs_contactEmail'],
        "customerContact.phone": messages['workOrder_historyLogs_contactPhone'],
        "driver.name": messages['workOrder_historyLogs_driver'],
        "status.name": messages['workOrder_historyLogs_status'],
        "data.comment": messages['workOrder_historyLogs_comment'],
        "previousStatusId": messages['workOrder_historyLogs_previousStatus'],
        "newStatusId": messages['workOrder_historyLogs_newStatus'],
        "createdOn": messages['workOrder_historyLogs_createdOn'],
        "address": messages['workOrder_historyLogs_address'],
        "scheduledDate": messages['workOrder_historyLogs_scheduledDate'],
        "estimatedDurationMins": messages['workOrder_historyLogs_estimatedDuration'],
        "type": messages['workOrder_historyLogs_type']
    };

    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, key) => {
            if (acc == null) return undefined;
            if (typeof acc === 'string') {
                try {
                    acc = JSON.parse(acc);
                } catch (e) {
                    return undefined;
                }
            }
            return acc[key];
        }, obj);
    }

    const stringifyFilteredObject = (obj, keys) => {
        const result = [];

        let jsonDetails = {};
        if (typeof obj.jsonDetails === 'string') {
            try {
                jsonDetails = JSON.parse(obj.jsonDetails);
            } catch (e) { }
        }

        for (const fullKey of Object.keys(keys)) {
            let value = getNestedValue(obj, fullKey);
            if (value == null) {
                value = getNestedValue(jsonDetails, fullKey);
            }
            if (value == null || value === '{}' || (typeof value === 'string' && value.trim() === '')) continue;
            const label = keyAliases[fullKey] || fullKey.split('.').pop();
            if (['newStatusId', 'previousStatusId'].includes(fullKey) && Array.isArray(workOrderStatus)) {
                const foundStatus = workOrderStatus.find(status => String(status.id) === String(value));
                if (foundStatus) {
                    result.push(`${label}: ${foundStatus.name}`);
                    continue;
                }
            }
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
                value = moment(value).format(`${format} hh:mm a`)
            }
            if (typeof value === 'string' && value === "departure") {
                value = messages['workOrder_historyLogs_departure']
            }
            if (typeof value === 'string' && value === "arrival") {
                value = messages['workOrder_historyLogs_arrival']
            }
            result.push(`${label}: ${value}`);
        }

        return result.join(', ');
    }

    const getDataToExport = (rows) => {
        let data = [];
        rows.forEach((row) => {
            let jsonDetails = ''
            try {
                jsonDetails = JSON.parse(row?.jsonDetails)
            } catch (error) { }

            const keys = {
                "nameCreatedBy.name": true,
                "scheduledDate": true,
                "customer.name": true,
                "customerLocation.name": true,
                "address": true,
                "type": true,
                "customerLocation.address.address": true,
                "customerContact.name": true,
                "customerContact.email": true,
                "customerContact.phone": true,
                "newStatusId": true,
                "previousStatusId": true,
                "workOrderType.name": true,
                "driver.name": true,
                "estimatedDurationMins": true,
                "severity": true,
                "description": true,
                "data.comment": true
            };

            let changes = stringifyFilteredObject(row, keys)
            let updatedOn = row?.updatedOn ? moment(row?.updatedOn).format(`${format} hh:mm a`) : ''

            if (!validateObject(jsonDetails)) {
                data.push([row?.nameCreatedBy?.name, updatedOn, messages[`workOrder_historyLogs_${jsonDetails?.action || row?.logType}`], changes ? splitTextIntoLines(changes, 60) : ""]);
            }
        });
        return data;
    };

    const exportCSVPdf = useExport(
        getHeaders,
        getDataToExport,
        [],
        historyLogs || [],
        null,
        `work-order-history-logs-${moment().unix()}`,
        "landscape",
        true
    );

    const onExport = (type) => {
        if (type === "Csv") {
            exportCSVPdf.exportCSV();
        } else if (type === "Pdf") {
            exportCSVPdf.exportPDF();
        }
    }

    return (
        <div className="work-orders-history-logs">
            {
                historyLogs?.length ? (
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
                        <TableButtons
                            messages={messages}
                            buttons={["export"]}
                            onExport={(type) => onExport(type)}
                        />
                    </div>
                ) : <></>
            }
            <div className="work-orders-history-logs-container">
                {
                    historyLogs.length ? historyLogs.map((item) => {
                        let data, address, textForm;
                        try {
                            data = JSON.parse(item?.jsonDetails || "{}");
                            address = item?.customerLocation?.address ? JSON.parse(item.customerLocation.address) : null;
                        } catch {
                            data = {};
                        }

                        if (validateObject(data)) return null;

                        if ((data?.action === "completedForm" || data?.action === "updatedForm") && data?.data?.length) {
                            textForm = data?.data[0]?.value
                        }

                        const newStatus = workOrderStatus.length ? workOrderStatus.find((s) => s.id === data?.newStatusId) : {}
                        const previousStatus = workOrderStatus.length ? workOrderStatus.find((s) => s.id === data?.previousStatusId) : {}

                        return (
                            <div key={item?.id} className="comments-item">
                                <Grid
                                    container
                                    direction="row"
                                    sx={{ justifyContent: "space-between", alignItems: "center" }}
                                >
                                    <Grid item xs={12} style={{ marginBottom: 5 }}>
                                        <span style={{ fontWeight: 700 }}>{item?.nameCreatedBy?.name || 'N/A'}</span>
                                    </Grid>
                                    {
                                        item?.updatedOn &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title" >{["updateWorkOrder", "changeStatus"].includes(data?.action) ? messages['workOrder_historyLogs_updatedOn'] : messages['workOrder_historyLogs_createdOn']}</span>
                                            <Timezone
                                                date={item?.updatedOn}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={timeZone}
                                            />
                                        </Grid>
                                    }
                                    {
                                        data && data?.action &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title" >{messages['workOrder_historyLogs_action']}</span>
                                            <span>{messages[`workOrder_historyLogs_${data.action}`]}</span>
                                        </Grid>
                                    }
                                    {
                                        textForm &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title" >Text</span>
                                            <span>{textForm}</span>
                                        </Grid>
                                    }
                                    {
                                        data?.scheduledDate &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title" >{messages['workOrder_historyLogs_scheduledDate']}</span>
                                            <Timezone
                                                date={data?.scheduledDate}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={timeZone}
                                            />
                                        </Grid>
                                    }
                                    {
                                        item?.customer?.name &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title" >{messages['workOrder_historyLogs_customer']}</span>
                                            <div className="comments-item-container" >
                                                <span>{item.customer.name}</span>
                                            </div>

                                        </Grid>
                                    }
                                    {
                                        item?.customerLocation?.name &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title" >{messages['workOrder_historyLogs_location']}</span>
                                            <div className="comments-item-container">
                                                <span>{`${item.customerLocation.name} / ${address?.address || 'N/A'}`}</span>
                                            </div>
                                        </Grid>
                                    }
                                    {
                                        item?.customerContact?.name &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title" >{messages['workOrder_historyLogs_contact']}</span>
                                            <div className="comments-item-container">
                                                <span>{`${item.customerContact.name} / ${item.customerContact.email} / ${item.customerContact.phone}`}</span>
                                            </div>
                                        </Grid>
                                    }
                                    {
                                        item?.workOrderType?.name &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title" >{messages['workOrder_historyLogs_type']}</span>
                                            <div className="comments-item-container">
                                                <span>{item?.workOrderType?.name}</span>
                                            </div>
                                        </Grid>
                                    }
                                    {
                                        item?.driver?.name && !data?.type &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title">{messages['workOrder_historyLogs_driver']}</span>
                                            <div className="comments-item-container">
                                                <span>{item.driver.name}</span>
                                            </div>
                                        </Grid>
                                    }
                                    {
                                        data?.estimatedDurationMins &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title">{messages['workOrder_historyLogs_estimatedDuration']}</span>
                                            <span>{`${data?.estimatedDurationMins} mins`}</span>
                                        </Grid>
                                    }
                                    {
                                        data?.severity &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title">{messages['workOrder_historyLogs_severity']}</span>
                                            <span>{data?.severity}</span>
                                        </Grid>
                                    }
                                    {
                                        data && data?.action === "changeStatus" &&
                                        <Grid item xs={12} style={{ display: 'flex', marginTop: 5 }}>
                                            <div style={{ display: 'flex' }}>
                                                <Pill
                                                    size="dinamic"
                                                    item={{ id: previousStatus?.id, label: previousStatus?.name, color: `#${previousStatus?.color}` }}
                                                />
                                                <Icon style={{ marginTop: 2 }}>arrow_forward</Icon>
                                                <Pill
                                                    size="dinamic"
                                                    item={{ id: newStatus?.id, label: newStatus?.name, color: `#${newStatus?.color}` }}
                                                    style={{ marginLeft: 4 }}
                                                />
                                            </div>
                                        </Grid>
                                    }
                                    {
                                        data && data?.action === "changeStatus" && data?.note &&
                                        <Grid item xs={12} style={{ wordBreak: 'break-word', width: 285 }}>
                                            <span className="comments-item-title">{messages['workOrder_historyLogs_note']}</span>
                                            <span>{data?.note}</span>
                                        </Grid>
                                    }
                                    {
                                        data?.description &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title">{messages['workOrder_historyLogs_description']}</span>
                                            <div className="comments-item-container">
                                                <span>{data?.description}</span>
                                            </div>
                                        </Grid>
                                    }
                                    {
                                        data?.data?.comment &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span className="comments-item-title">{messages['workOrder_historyLogs_comment']}</span>
                                            <div className="comments-item-container">
                                                <span>{`${data?.data?.comment}`}</span>
                                            </div>
                                        </Grid>
                                    }
                                    {
                                        data?.type === "arrival" &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span>{messages['workOrder_historyLogs_arrival']}</span>
                                        </Grid>
                                    }
                                    {
                                        data?.type === "departure" &&
                                        <Grid item xs={12} style={{ display: 'flex' }}>
                                            <span>{messages['workOrder_historyLogs_departure']}</span>
                                        </Grid>
                                    }
                                    {
                                        item?.logType === "gpsEvent" && data?.address &&
                                        <Grid item xs={12} style={{ display: 'flex' }} onClick={() => {
                                            let payload = {
                                                show: true,
                                                title: item?.nameCreatedBy?.name || messages['workOrder_historyLogs_address'],
                                                isPointerMarker: true,
                                                pointers: [
                                                    {
                                                        latitude: data.latitude,
                                                        longitude: data.longitude,
                                                        label: "Map Point",
                                                        heading: 0,
                                                        color: "000",
                                                        eventName: data?.eventName,
                                                        draggable: false
                                                    }
                                                ]
                                            };
                                            setDataToMap(payload);
                                        }}>
                                            <span className="comments-item-title">{messages['workOrder_historyLogs_address']}</span>
                                            <div className="comments-item-container" style={{ display: 'flex' }}>
                                                <Icon style={{ marginTop: 3, fontSize: 14 }}>room</Icon>
                                                <span style={{ textDecoration: 'underline' }}>{`${data?.address}`}</span>
                                            </div>
                                        </Grid>
                                    }
                                </Grid>
                            </div>
                        )
                    }) : <div style={{ textAlign: 'center', opacity: 0.6, marginTop: 15 }}> {messages['no_data']}</div>
                }
            </div>
            {
                dataToMap?.show &&
                <MapModal
                    hiddenButton
                    {...dataToMap}
                    hiddenTraffic={true}
                    hiddenWeather={true}
                    hiddenSearchMap={true}
                    hideModal={() => {
                        setDataToMap({ show: false });
                    }}
                    zoom={18}
                />
            }
        </div>
    )
}


export default HistoryLogs